import VisibilityIcon from '@mui/icons-material/Visibility';
import {
	Box,
	Button,
	ButtonGroup,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
	Grid
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import AceEditor from 'react-ace';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Input from '@mui/material/Input';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import strings from '../Globalization/index';
import { dataStore } from '../Store';
import ActionIconButton from '../components/common/ActionIconButton';
import LoadingDialog from '../components/common/LoadingDialog';
import consts from '../consts';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useSetState } from '../hooks';
import Model from '../models/model';
import connectionService from '../services/Connection';
import util from '../util';
import PrintProperties from '../views/PrintProperties';
import 'ace-builds/src-noconflict/mode-xml';
import 'ace-builds/src-noconflict/theme-github';

const { regex } = consts;
const isButtonNeedToDisplay = ['steris admin', 'Steris Specialist']

const readOnlyRoles = ["huluadmin", "operator", "department", "supervisor", 'steris specialist'];
const ViewXMlValidation = () => {
	const { auth = {} } = dataStore.getState();
	const { tags = {} } = auth;
	return readOnlyRoles.includes(tags.RoleName.toLowerCase()) || (window.location.href.includes('endoCycleslist'));
}
const removeColumnOnGrid = ({ location }) => {
	const { auth = {} } = dataStore.getState();
	const { tags = {} } = auth;
	return location.pathname?.toLowerCase?.() === '/endocycleslist' || !tags.ProductType.includes('catviewer')
}
const excludeEquipmentTypeFilter = ({ location }) => {
	return location.pathname?.toLowerCase?.() === '/endocycleslist'
}
const printProperties = new PrintProperties();
const { bIResultRenderer } = util;
export const ProcessXmlFormDialog = () => {
	const navigate = useNavigate();
	const [, { getLicenseType }] = usePrerequisites();
	const licenceType = typeof getLicenseType === 'function' ? getLicenseType() : {};
	const isLimitedLicense =
		((licenceType?.catexport || licenceType?.catsync) && !licenceType?.multi) ||
		(licenceType?.catexport && licenceType?.catsync && licenceType?.products?.length === 2);;

	const [state, setState] = useSetState({
		open: false,
		xmlEditorValue: '',
		xmlPopupValue: '',
		loading: false,
		fileName: false
	});
	const isViewXml = ViewXMlValidation();

	const onClose = () => {
		setState({ open: false, fileName: '', xmlEditorValue: '' });
	}

	const onxmlValueChange = (value) => {
		setState({ xmlEditorValue: value });
	};

	const handleSubmit = useCallback(
		(event) => {
			event.preventDefault()
			setState({ loading: true });
			const parser = new DOMParser();
			const text = state.xmlEditorValue;
			setState({ xmlPopupValue: text });
			const xmlDoc = parser.parseFromString(text, 'text/xml');
			if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
				setState({ loading: false });
				Swal.fire(strings.Alert, strings.InvalidXML, 'error');
			} else {
				const formData = new FormData();
				formData.append('action', 'otherAction');
				formData.append('otherAction', 'ProcessPreviewXML');
				formData.append('xmlData', text);
				fetch(connectionService.CycleList, { method: 'POST', body: formData })
					.then(res => res.json())
					.then(json => {
						if (json.data !== null) {
							const record = JSON.parse(json.data);
							const cycleId = record.CycleId;
							const incubatorCycleId = record.IncubatorCycleId;
							const sterilizerCycleId = record.SterilizerCycleId;
							const advantageCycleId = record.AdvantageCycleId;
							const isEnspire = (record.EnspireEquipmentFamilyId || '') === consts.EquipmentFamilyType.LIQUIDCHEMICALENSPIRE3000;
							if (record.IsIncubatorCycle && incubatorCycleId) {
								Swal.fire({
									title: strings.Info,
									text: strings.IncubatorCycleIsProcessedButCycleIsNotAvailableAsView,
									icon: 'info',
									confirmButtonText: strings.Ok,
									allowOutsideClick: false
								});
								document.getElementById('xmlFile').value = '';
							} else if (record.IsSterilizerCycle && sterilizerCycleId) {
								navigate(
									`/sterilizerCycleinfo?id=${sterilizerCycleId}&isFromProcessXml=true&isSteam=${record.IsSteam}&IsSteamEU=${record.IsSteamEU}&IsAMSCOEU=${record.IsAMSCOEU}`
								);
							} else if(isEnspire){
								navigate(`/enspirecycleinfo?id=${record.EndoSystemCycleId}&equipmentFamilyId=${consts.EquipmentFamilyType.ENSPIRE3000}&equipmentId=0&attachmentCount=0&isFromProcessXml=true`);
							} else if (record.IsLiquidChemical) {
								navigate(`/endosystemcycleinfo?id=${record.EndoSystemCycleId}&isFromProcessXml=true`);
							} else if (record.isAdvantage && advantageCycleId) {
								navigate(`/advantageCycleinfo?id=${advantageCycleId}&isFromProcessXml=true`);
							} else if (cycleId) {
								navigate(
									`/cycleinfo?id=${cycleId}&isFromProcessXml=true&isWasher=${record.IsWasher}&isUltraSonic=${record.IsUltraSonic}`
								);
							} else {
								Swal.fire({ title: strings.Alert, text: strings.XmlProcessingFailed, icon: 'error', confirmButtonText: strings.OK, allowOutsideClick: false });
							}
						} else {
							Swal.fire({ title: strings.Alert, text: strings.XmlProcessingFailed, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
						}
						setState({ loading: false });
					}).catch(err => {
						setState({ loading: false });
					});
			}

		},
		[state.xmlEditorValue]
	);

	const onFileRead = (event) => {
		const file = event.currentTarget.files[0];
		if (file instanceof File) {
			const reader = new FileReader();
			reader.readAsText(file, 'UTF-8');
			reader.onload = function (evt) {
				setState({ xmlEditorValue: evt.target.result, xmlPopupValue: '', fileName: `${file.name}` });
			};
			reader.onerror = function (evt) {
				Swal.fire({
					icon: 'error',
					title: strings.Alert,
					text: strings.ErrorReadingFile,
					confirmButtonText: strings.OK,
					allowOutsideClick: false
				});
			};
		}
	}
	return (
		<Box >
			<Dialog
				component="form"
				onSubmit={handleSubmit}
				open={state.open}
				onClose={onClose}
				sx={{ '& .MuiDialog-paper': { minWidth: '70vw' } }}
			>
				<DialogTitle>
					<Stack direction="row" sx={{ width: '100%' }}>
						<Typography sx={{ flex: 1 }}>{strings.EnterXMLtoProcess}</Typography>
					</Stack>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={1}>
						<Grid size={12}>
							<AceEditor
								mode="xml"
								theme="github"
								name="xml"
								editorProps={{ $blockScrolling: true }}
								width={'100%'}
								onChange={onxmlValueChange}
								value={state.xmlEditorValue}
							/>
						</Grid>
						<Grid size={12}>
							<ButtonGroup>
								<Button type="submit" onClick={handleSubmit} variant="contained" sx={{ mr: 1 }} color="success">
									{strings.Ok}
								</Button>
								<Button type="button" onClick={onClose} variant="contained" sx={{ mr: 1 }} color="warning">
									{strings.Cancel}
								</Button>
							</ButtonGroup>
						</Grid>
						<Grid size={12}>
							<Input type="file" id="xmlFile" inputProps={{ accept: '.xml' }} onChange={onFileRead} style={{ display: 'none' }} />
							<label htmlFor="xmlFile">
								<Button
									variant="outlined"
									component="span"
									style={{ padding: '0px 18px', height: '30px' }}
								>
									{strings.ChooseFile}
								</Button>
							</label>
							<Typography varient="body1" component="span" inline="true" sx={{ mx: 1 }}>{state.fileName}</Typography>
						</Grid>
					</Grid>
					<LoadingDialog open={state.loading} />
				</DialogContent>
				<DialogActions></DialogActions>
			</Dialog>
			{(!isLimitedLicense && !isViewXml) && (
				<ActionIconButton
					onClick={() => setState({ open: true })}
					color="primary"
					variant="contained"
					title={strings.TestXML}
				>
					<VisibilityIcon fontSize="small" />
				</ActionIconButton>
			)}
		</Box>
	);
};

export const MonitoringStatusCell = ({ row }) => {
	if (!Object.prototype.hasOwnProperty.call(row, 'ProcessMonitoringStatus')) {
		return
	}
	return (
		<Box className="columnIcon">
			{row.ProcessMonitoringStatus ? (
				<CheckIcon fontSize="large" color="success" />
			) : (
				<CloseIcon fontSize="large" color="error" />
			)}
		</Box>
	);
}
export const CycleHistory = () => {
	const { auth = {} } = dataStore?.getState() || {};
	const { tags = {} } = auth;
	return isButtonNeedToDisplay.includes(tags?.RoleName?.toLowerCase()) && !window.location.href.includes('endoCycleslist') && !window.location.href.includes('dashboardcycles') && (
		<Button startIcon={<HistoryIcon />} color='success' component={Link} size="small" to={`/trackcycle`} variant="contained" >{strings.cycleHistory || 'cycleHistory'}</Button>
	);
}

const currentRoute = () => {
	if (window.location.href.includes('cyclelist')) {
		return false;
	}
	return true
}

const getEndoValue = (value) =>
	typeof value === 'string'
		? value
			.split(',')
			.filter((val) => val.trim().length > 0 && val !== 'undefined')
			.join(',')
		: value

export const model = new Model({
	name: 'Cycle',
	api: 'Cycle',
	idProperty: 'id',
	defaultSort: 'CycleStart DESC',
	disableAdd: true,
	disableDelete: true,
	formTitleField: 'CycleName',
	gridRefreshInterval: 300000,
	showGridRefreshBtn: true,
	gridToolbarCustomActions: [
		{
			Component: CycleHistory
		}
	],
	combos: [
		{ type: 'CycleActionStatus', loaded: true },
		{ type: 'CycleStatus', loaded: true },
		{ type: 'Location', loaded: true },
		{ type: 'EquipmentType', loaded: true },
		{ type: 'Indicator', loaded: true },
		{ type: 'TestOrControl', loaded: true }
	],
	columns: [
		{ field: 'CycleId', header: 'UniqueId', width: 250, hide: true },
		{ field: 'EquipmentName', header: 'EquipmentName', width: 150 },
		{
			field: 'EquipmentTypeName',
			header: 'EquipmentTypeName',
			width: 150,
			hide: ({ location }) => consts.endoManagementRoute.includes(location.pathname)
		},
		{ field: 'ModelName', header: 'ModalName', width: 200 },
		{ field: 'SerialNumber', header: 'SerialNumber', width: 180 },
		{
			field: 'MonitoringStatus',
			align: 'center',
			header: 'ProcessMonitoring',
			type: 'action',
			width: 150,
			excludeOnGrid: ({ auth, location }) => {
				const loadRelease = auth?.tags?.LoadReleaseId;
				return loadRelease !== consts.LoadReleaseType.Parametric || removeColumnOnGrid({ location });
			},
			renderCell: MonitoringStatusCell
		},

		{ field: 'CycleName', header: 'CycleName', width: 190 },
		{ field: 'CycleCount', header: 'CycleCount', type: 'number', width: 150, valueFormatter: (value) => value?.toString().replace(/,/g, '') },
		{ field: 'CycleStatus', header: 'SterCycleStatus', width: 150, renderCell: bIResultRenderer },
		{
			field: 'CycleStart',
			header: 'CycleDateTime',
			type: 'dateTime',
			width: 200
		},

		{
			field: 'InstrumentSN',
			header: 'EndoscopeID',
			width: 150,
			hide: true,
			valueGetter: getEndoValue,
			excludeOnGrid: currentRoute ? false : removeColumnOnGrid
		},

		{ field: 'ErrorDescription', header: 'AlarmDescription', width: 260, excludeOnGrid: removeColumnOnGrid },
		{
			field: 'Label',
			header: 'Label',
			width: 120,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'InstrumentName',
			header: 'Endoscope',
			width: 160,
			filterable: false,
			valueGetter: getEndoValue, //Remove the last comma
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'ModelNumber',
			header: 'EndoscopeType',
			width: 180,
			valueGetter: getEndoValue,
			hide: currentRoute ? false : removeColumnOnGrid
		},
		{
			field: 'InstrumentBarcode',
			header: 'EndoscopeBarcode',
			width: 160,
			excludeOnGrid: ({ location }) => location.pathname.includes('/cyclelist'),
			valueGetter: getEndoValue 
		},

		{ field: 'BIResult', header: 'BIResult', width: 200, type: 'int', hidden: true, excludeOnGrid: removeColumnOnGrid },
		{
			field: 'AttachFile',
			header: 'AttachFile',
			width: 120,
			filterable: false,
			excludeOnGrid: removeColumnOnGrid,
			renderCell: ({ row }) =>
				util.renderAttachmentButton(row, row.EquipmentTypeId === consts.EquipmentType.STERILIZER)
		},
		{ field: 'WasherCILotNumber', header: 'CILotNumber', width: 85, excludeOnGrid: true },
		{ field: 'WasherCIResultIcon', header: 'CIResult', width: 85, type: 'action', excludeOnGrid: true },
		{ field: 'STSLotNumber', header: 'STSLotNumber', width: 85, excludeOnGrid: true },
		{ field: 'STSResultIcon', header: 'STSResult', width: 85, type: 'action', excludeOnGrid: true },
		{ field: 'LCSCILotNumber', header: 'CILotNumber', width: 85, excludeOnGrid: true },
		{ field: 'LCSCIResultIcon', header: 'CIResult', width: 85, type: 'action', excludeOnGrid: true },
		{
			field: consts.isNodeServer ? 'ReviewStatus' : 'CycleActionStatus',
			header: 'Action',
			width: 150,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: consts.isNodeServer ? 'ReviewComment' : 'Comment',
			header: 'Comment',
			width: 180,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: consts.isNodeServer ? 'ReviewDate' : 'ApprovedOn',
			header: 'ApprovedOn',
			width: 145,
			type: 'dateTime',
			isLocal: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: consts.isNodeServer ? 'ReviewByUser' : 'Approver',
			header: 'ApprovedBy',
			width: 140,
			excludeOnGrid: removeColumnOnGrid
		},
		{ field: 'EndoscopeTopName', header: 'EndoscopeTopName', width: 120, hide: true, excludeOnGrid: removeColumnOnGrid },
		{
			field: 'EndoscopeBottomName',
			header: 'EndoscopeBottomName',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'EndoscopeSerialNumber',
			header: 'EndoscopeSerialNumber',
			width: 180,
			excludeOnPanelColumns: ({ location }) => location.pathname.includes('/cyclelist'),
			hide: ({ location }) => location.pathname.includes('/cyclelist'),
			valueGetter: getEndoValue
		},
		{
			field: 'EndoscopeSerialNumberTop',
			header: 'EndoscopeSerialNumberTop',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'EndoscopeSerialNumberBottom',
			header: 'EndoscopeSerialNumberBottom',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'EndoscopeModelTop',
			header: 'EndoscopeModelTop',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'EndoscopeModelBottom',
			header: 'EndoscopeModelBottom',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{ field: 'ItemRefNumberTop', header: 'TopOrderNumber', width: 120, hide: true, excludeOnGrid: removeColumnOnGrid },
		{
			field: 'ItemRefNumberBottom',
			header: 'BottomOrderNumber',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'OperatorName',
			header: 'OperatorName',
			width: 260
		},
		{
			field: 'OperatorID',
			header: 'OperatorId',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'UnloadOperatorName',
			header: 'UnloadOperatorName',
			width: 150,
			hide: !removeColumnOnGrid
		},
		{
			field: 'UnloadOperatorID',
			header: 'UnloadOperatorID',
			width: 120,
			hide: true,
			excludeOnGrid: removeColumnOnGrid
		},
		{
			field: 'LocationId',
			header: 'Location',
			type: 'string',
			lookup: 'Location',
			hide: true,
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'EquipmentType',
			header: 'EquipmentType',
			type: 'string',
			lookup: 'EquipmentType',
			multiple: true,
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			excludeOnGrid: excludeEquipmentTypeFilter,
			autocompleteValueFormatter: (value) => {
				let formatedValue = '';
				if (Array.isArray(value)) {
					formatedValue = value.map((entry) => entry?.id || entry).join(',');
				}
				return formatedValue;
			},
			autocompleteValueParser: (value, options) => {
				let parsedValue = [];
				try {
					if (typeof value === 'string') {
						parsedValue = value.replace('[', '').replace(']', '').split(',');
					} else if (Array.isArray(value)) {
						parsedValue = value;
					}
				} catch (error) { /* empty */ }

				return parsedValue;
			},
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'startDate',
			header: 'StartDate',
			type: 'date',
			format: consts.MomentFormat.MonthDateYear,
			maxDate: ({ formik }) => (formik.values.endDate ? dayjs(formik.values.endDate) : dayjs()),
			hide: true,
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'endDate',
			header: 'EndDate',
			type: 'date',
			format: consts.MomentFormat.MonthDateYear,
			minDate: ({ formik }) => (formik.values.startDate ? dayjs(formik.values.startDate) : undefined),
			maxDate: () => dayjs(),
			headerFilter: true,
			standaloneHeaderFilter: true,
			isNotFormfield: true,
			headerFilterWrapperProps: {
				xs: 6,
				container: true
			}
		},
		{
			field: 'PrintTape',
			header: 'PreviewTape',
			width: 120,
			excludeOnGrid: ({ location }) => ['/cyclelist', '/dashboardcycles'].includes(location.pathname),
			renderCell: ({ row }) => (
				<Button variant="contained" title="Preview Tape" size="small" disabled={!row?.CycleTape}>
					{strings.PreviewTape}
				</Button>
			)
		}
	],
	headerFilterProps: {
		actions: [
			{
				Component: Box,
				children: [<ProcessXmlFormDialog key='processXml'/>]
			}
		]
	},
	extraExportConfig: {
		PDF: {
			pageSize: 'A2'
		}
	}
});

export default model.createFormBase();